import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Quote } from 'lucide-react';
import { Reveal } from '../components/Shared';
import { CaseStudyCard } from './CaseStudies';
import { publicApi, mediaUrl } from '../lib/publicApi';
import { useSeo } from '../hooks/useSeo';

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, study: null, more: [], missing: false });

  useEffect(() => {
    let active = true;
    setState({ loading: true, study: null, more: [], missing: false });
    publicApi.caseStudy(slug)
      .then(data => { if (active) setState({ loading: false, study: data.caseStudy, more: data.more, missing: false }); })
      .catch(err => { if (active) setState({ loading: false, study: null, more: [], missing: err.message === 'not-found' }); });
    return () => { active = false; };
  }, [slug]);

  const { study, more, loading, missing } = state;

  useSeo(useMemo(() => (study ? {
    title: study.seo_title || `${study.client_name} — TechSpitze Case Study`,
    description: study.seo_description || study.summary,
    image: mediaUrl(study.cover_image),
    jsonLd: {
      '@context': 'https://schema.org', '@type': 'CreativeWork', name: study.title || study.client_name,
      about: study.industry, headline: study.headline_metric, description: study.seo_description || study.summary,
      image: mediaUrl(study.cover_image), dateModified: study.updated_at,
      creator: { '@type': 'Organization', name: 'TechSpitze' },
      keywords: (study.tech_stack || []).join(', '),
    },
  } : {}), [study]));

  if (loading) return <main id="main-content" className="article-page container" data-testid="study-loading"><div className="article-skeleton" /><div className="article-skeleton short" /></main>;
  if (missing || !study) return <main id="main-content" className="container not-found" data-testid="study-not-found">
    <span className="eyebrow">404 / CASE STUDY NOT FOUND</span>
    <h1>That story<br /><span className="heading-accent">isn’t published.</span></h1>
    <Link className="action-button button-primary" to="/case-studies" data-testid="study-back-to-list">All case studies ↗</Link>
  </main>;

  return <main id="main-content" className="study-detail" data-testid="study-detail-page">
    <section className="container study-hero">
      <Link to="/case-studies" className="article-back" data-testid="study-back"><ArrowLeft size={15} />All case studies</Link>
      <span className="article-category" data-testid="study-industry">{study.industry}</span>
      <h1 data-testid="study-client">{study.client_name}</h1>
      {study.headline_metric && <p className="study-headline-metric" data-testid="study-headline-metric">{study.headline_metric}</p>}
      <p className="study-summary" data-testid="study-summary">{study.summary}</p>
      {study.pillars?.length > 0 && <div className="tag-row" data-testid="study-pillars">{study.pillars.map(p => <span className="tag-chip" key={p}>{p}</span>)}</div>}
    </section>

    {study.cover_image && <div className="container article-cover" data-testid="study-cover"><img src={mediaUrl(study.cover_image)} alt={study.client_name} loading="lazy" /></div>}

    <section className="container study-sections">
      {study.challenge && <Reveal className="study-block" data-testid="study-challenge"><h2>The challenge</h2><div className="rich-text" dangerouslySetInnerHTML={{ __html: study.challenge }} /></Reveal>}
      {study.solution && <Reveal className="study-block" data-testid="study-solution"><h2>Our solution</h2><div className="rich-text" dangerouslySetInnerHTML={{ __html: study.solution }} /></Reveal>}
      {study.tech_stack?.length > 0 && <Reveal className="study-block" data-testid="study-tech"><h2>Tech stack</h2><div className="tag-row">{study.tech_stack.map(t => <span className="tag-chip" key={t}>{t}</span>)}</div></Reveal>}
      {study.metrics?.length > 0 && <Reveal className="study-block" data-testid="study-metrics"><h2>Results</h2>
        <div className="metric-tiles">{study.metrics.map(metric => <div className="metric-tile" key={metric.label}><strong>{metric.value}</strong><span>{metric.label}</span></div>)}</div>
        {study.results && <div className="rich-text" dangerouslySetInnerHTML={{ __html: study.results }} />}
      </Reveal>}
      {study.gallery?.length > 0 && <Reveal className="study-block" data-testid="study-gallery"><h2>Gallery</h2>
        <div className="study-gallery">{study.gallery.map((src, i) => <img key={src + i} src={mediaUrl(src)} alt={`${study.client_name} gallery image ${i + 1}`} loading="lazy" />)}</div>
      </Reveal>}
      {study.testimonial?.quote && <Reveal className="study-quote glass" data-testid="study-testimonial">
        <Quote size={26} aria-hidden="true" />
        <blockquote>{study.testimonial.quote}</blockquote>
        <span>{study.testimonial.name}{study.testimonial.title ? ` · ${study.testimonial.title}` : ''}</span>
      </Reveal>}
    </section>

    <section className="container content-cta" data-testid="study-cta">
      <div className="content-cta-band glass">
        <div><span className="eyebrow"><span className="eyebrow-line" />YOUR TURN</span><h2>Have a similar challenge?</h2></div>
        <Link to="/intake-form" className="action-button button-primary" data-testid="study-cta-button">Start the intake form <ArrowUpRight size={17} /></Link>
      </div>
    </section>

    {more.length > 0 && <section className="container article-related" data-testid="study-more">
      <h2>More case studies</h2>
      <div className="post-grid study-grid">{more.map(item => <Reveal key={item.slug}><CaseStudyCard study={item} /></Reveal>)}</div>
    </section>}
  </main>;
}
